import React from 'react';
import PropTypes from 'prop-types';
import PlayerPhoto from '../shared/PlayerPhoto';
import TrendIndicator from '../shared/TrendIndicator';
import EligibilityBadges from '../../../../EligibilityBadges';
import { formatContractDisplay } from '../../../../../services/contractUtils';
import { dynastyTheme } from '../../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

/**
 * CardHeroSection - Condensing hero section for MobilePlayerCard
 * 
 * Features:
 * - Expands/condenses based on scroll (120px → 60px)
 * - Player photo (large → small)
 * - Player info (full → minimal)
 * - Trend indicator
 * - Smooth transitions
 * - Sticky positioning
 * 
 * Used in: MobilePlayerCard
 */

const CardHeroSection = ({ player, height, isCondensed }) => {
  // Determine photo size based on condensed state (use actual dimensions)
  const photoSize = isCondensed ? '60x60' : '100x100';

  if (!player) {
    return (
      <div style={{ ...styles.hero, height: `${height}px` }}>
        <div style={styles.loadingSkeleton}>
          <div style={styles.skeletonPhoto} />
          <div style={styles.skeletonText} />
        </div>
      </div>
    );
  }

  // Extract canonical paths
  const mlbId = player.ids?.mlb;
  const name = player.info?.full_name;
  const position = player.info?.position;
  const team = player.info?.mlb_team;
  const jerseyNumber = player.info?.jersey_number;
  const trend = player.analytics?.trend;
  const financial = player.league_context?.financial;

  // Contract — only show if player is owned and has valid contract values
  const isOwned = player.league_context?.status === 'owned';
  const contractSalary = financial?.contract_salary;
  const contractYears = financial?.contract_years;
  const contractType = financial?.contract_type;
  const showContractInfo = isOwned && contractSalary > 0 && contractYears > 0;

  // Hot/Cold status — from backend analytics.hot_cold ONLY
  const hotColdStatus = (() => {
    const hotCold = player.analytics?.hot_cold;
    if (!hotCold) return { emoji: '➖', status: 'STEADY', color: C.coolGray[400] };
    const status = hotCold.status?.toUpperCase() || 'STEADY';
    const emoji = hotCold.temperature || '➖';
    let color = C.coolGray[400];
    if (hotCold.status === 'hot') color = C.error;
    else if (hotCold.status === 'cold') color = C.info;
    else if (hotCold.status === 'warm') color = C.orange.base;
    else if (hotCold.status === 'cool') color = C.cyan;
    return { emoji, status, color };
  })();

  return (
    <div style={{ ...styles.hero, height: `${height}px` }}>
      <div style={styles.heroContent}>
        {/* Left Side - Photo */}
        <div style={styles.photoContainer}>
          <PlayerPhoto
            mlbId={mlbId}
            size={photoSize}
          />
        </div>

        {/* Center - Player Info */}
        {isCondensed ? (
          // Condensed View (60px)
          <div style={styles.infoCondensed}>
            <div style={styles.nameCondensed}>{name || ''}</div>
            <div style={styles.positionTeamCondensed}>
              {position} • {team}
            </div>
          </div>
        ) : (
          // Full View (120px)
          <div style={styles.infoFull}>
            <div style={styles.nameFull}>
              {name || ''}
              {jerseyNumber && (
                <span style={styles.jersey}>#{jerseyNumber}</span>
              )}
            </div>
            <div style={styles.positionTeamFull}>
              {position} • {team}
            </div>
            <div style={{ marginTop: '2px' }}>
              <EligibilityBadges player={player} size="sm" />
            </div>
            {/* Contract - only show if player is owned and has valid contract values */}
            {showContractInfo && (
              <div style={styles.contractInfo}>
                ${contractSalary}M • {formatContractDisplay(contractType, contractYears)}
              </div>
            )}

            {/* Price and MV - show for ALL players */}
            {(financial?.baseline_price != null || financial?.current_market_value != null) ? (
              <div style={styles.priceMVInfo}>
                {financial?.baseline_price != null ? (
                  <span style={styles.priceText}>
                    Price: ${financial.baseline_price}M
                  </span>
                ) : null}
                {financial?.current_market_value > 0 ? (
                  <span style={styles.mvText}>
                    MV: ${financial.current_market_value}M
                  </span>
                ) : (
                  <span style={{...styles.mvText, color: C.coolGray[500]}}>
                    MV: N/A
                  </span>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Right Side - Hot/Cold Status */}
        {hotColdStatus && (
          <div style={styles.hotColdContainer}>
            <div style={{ ...styles.hotColdEmoji, fontSize: isCondensed ? '20px' : '24px' }}>
              {hotColdStatus.emoji}
            </div>
            {!isCondensed && (
              <div style={{ ...styles.hotColdText, color: hotColdStatus.color }}>
                {hotColdStatus.status}
              </div>
            )}
          </div>
        )}

        {/* Right Side - Trend Indicator (if no hot/cold) */}
        {!hotColdStatus && trend && trend.status && (
          <div style={styles.trendContainer}>
            <TrendIndicator
              trend={trend.status}
              percentage={trend.percentage || 0}
              size={isCondensed ? 'compact' : 'standard'}
            />
          </div>
        )}
      </div>
    </div>
  );
};

CardHeroSection.propTypes = {
  player: PropTypes.shape({
    ids: PropTypes.shape({ mlb: PropTypes.number }),
    info: PropTypes.shape({
      full_name: PropTypes.string,
      position: PropTypes.string,
      mlb_team: PropTypes.string,
      jersey_number: PropTypes.number,
    }),
    analytics: PropTypes.shape({
      trend: PropTypes.shape({
        status: PropTypes.string,
        percentage: PropTypes.number,
      }),
      hot_cold: PropTypes.shape({
        status: PropTypes.string,
        temperature: PropTypes.string,
      }),
    }),
    league_context: PropTypes.shape({
      status: PropTypes.string,
      financial: PropTypes.shape({
        contract_salary: PropTypes.number,
        contract_years: PropTypes.number,
        contract_type: PropTypes.string,
        baseline_price: PropTypes.number,
        current_market_value: PropTypes.number,
      }),
    }),
    stats: PropTypes.object,
  }),
  height: PropTypes.number.isRequired,
  isCondensed: PropTypes.bool.isRequired,
};

CardHeroSection.defaultProps = {
  player: null,
};

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  hero: {
    position: 'sticky',
    top: '56px', // Below top bar
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    background: `linear-gradient(180deg, ${C.surface[800]} 0%, ${C.surface[855]} 100%)`,
    borderBottom: `1px solid ${C.surface[900]}`,
    transition: 'height 0.2s ease',
    zIndex: 90,
  },

  heroContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '16px',
  },

  // Photo Container
  photoContainer: {
    flexShrink: 0,
  },

  // Full Info (120px height)
  infoFull: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0, // Allow text truncation
  },

  nameFull: {
    fontSize: '20px',
    fontWeight: '600',
    color: C.white,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  jersey: {
    fontSize: '16px',
    fontWeight: '500',
    color: C.coolGray[400],
  },

  positionTeamFull: {
    fontSize: '14px',
    color: C.coolGray[400],
  },

  contractInfo: {
    fontSize: '12px',
    color: C.coolGray[400],
  },

  priceMVInfo: {
    fontSize: '11px',
    fontWeight: '600',
    color: C.coolGray[400],
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '2px'
  },

  priceText: {
    color: C.orange.base, // Orange/warning color for price
  },

  mvText: {
    color: C.green.bright, // Green/success color for MV
  },

  // Condensed Info (60px height)
  infoCondensed: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },

  nameCondensed: {
    fontSize: '16px',
    fontWeight: '600',
    color: C.white,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  positionTeamCondensed: {
    fontSize: '12px',
    color: C.coolGray[400],
  },

  // Hot/Cold Container
  hotColdContainer: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
  },

  hotColdEmoji: {
    lineHeight: 1,
  },

  hotColdText: {
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Trend Container
  trendContainer: {
    flexShrink: 0,
  },

  // Loading States
  loadingSkeleton: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
  },

  skeletonPhoto: {
    width: '100px',
    height: '100px',
    borderRadius: '8px',
    background: `linear-gradient(90deg, ${C.surface[855]} 0%, ${C.surface[900]} 50%, ${C.surface[855]} 100%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },

  skeletonText: {
    flex: 1,
    height: '60px',
    borderRadius: '8px',
    background: `linear-gradient(90deg, ${C.surface[855]} 0%, ${C.surface[900]} 50%, ${C.surface[855]} 100%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  },
};

// Add shimmer animation
if (typeof window !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default CardHeroSection;
